import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { Avatar } from "@faire/web--source/slate/Avatar";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { IRetailerBrandFollow } from "@faire/web-api--source/indigofair/data/IRetailerBrandFollow";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { BrandOverallRating } from "@faire/retailer/components/RatingStars/BrandOverallRating";
import { getBrandLocationString } from "@faire/retailer/consts/getBrandLocationString";
import { Tag } from "@faire/retailer/views/brand/components/Tag";
import { BrandMinimumInfo } from "@faire/retailer/views/faire-direct/__internal__/BrandMinimumInfo";

import { ProductPreviewCarousel } from "./ProductPreviewCarousel";

interface IProps {
  item: IRetailerBrandFollow;
  brand: IBrand;
  productTiles: ILeanProductTile[];
  totalProductCount?: number;
  loadTimingRef?: React.Ref<HTMLDivElement>;
}

const BrandPreviewBase: React.FC<IProps> = ({
  item,
  brand,
  totalProductCount,
  productTiles,
  loadTimingRef,
}) => {
  const { isTabletAndAbove } = useViewport();
  const { strictLocalize } = useStrictLocalization();

  const basedIn = getBrandLocationString(
    {
      city: item.brand_based_in_city,
      state: item.brand_based_in_state,
      addBasedIn: true,
    },
    strictLocalize
  );

  const averageRating = item.brand_reviews_summary?.average_rating ?? 0;

  const extraProductCount = totalProductCount
    ? totalProductCount - productTiles.length
    : 0;

  return (
    <Wrapper>
      <PictureContainer>
        <PictureOverlay />
        <Avatar
          image={item.brand_profile_image}
          size={isTabletAndAbove ? "x-large" : "large"}
        />
      </PictureContainer>
      <BrandInfoArea>
        <LabelContainer
          variant="displaySSerifRegular"
          data-test-id="productGridHeader"
        >
          {item.brand_name}
        </LabelContainer>
        <Spacer height="1x" />
        <Typography color={Core.text.subdued}>
          {basedIn ? (
            <>
              {basedIn}
              <br />
            </>
          ) : null}
          <BrandMinimumInfo
            brand_first_order_minimum={item.brand_first_order_minimum}
            brand_reorder_minimum={item.brand_reorder_minimum}
          />
        </Typography>
        {averageRating >= 4 ? (
          <>
            <Spacer height="1x" />
            <BrandOverallRating rating={averageRating} />
          </>
        ) : null}
        <Spacer height="1x" />
        <Row style={{ margin: "0 0 -8px 0", flexWrap: "wrap" }}>
          {brand.tags.map(({ tag, token }) => (
            <Tag key={token}>
              <Typography>{tag}</Typography>
            </Tag>
          ))}
        </Row>
      </BrandInfoArea>
      <ProductsTitle variant="displaySSerifRegular">
        <StrictLocalMsg defaultMessage="Featured Products" />
      </ProductsTitle>
      <ProductPreviewCarouselWrapper ref={loadTimingRef}>
        <ProductPreviewCarousel
          brandToken={item.brand_token!}
          productTiles={productTiles}
          moreProductCount={extraProductCount}
        />
      </ProductPreviewCarouselWrapper>
    </Wrapper>
  );
};

export const BrandPreview = observer(BrandPreviewBase);

const Wrapper = styled.li`
  display: grid;
  grid-column-gap: 16px;
  position: relative;
  grid-template:
    "profile-picture  brand-info      "
    "profile-picture  .               " 16px
    "profile-picture  .               "
    "products-title   products-title  " 24px
    ".                .               " 24px
    "product-list     product-list    "
    / 64px 1fr;

  ${responsiveMediaQueries.tabletAndAbove} {
    grid-template:
      "profile-picture  brand-info      ."
      ".                .               ." 24px
      "products-title   products-title  ." 24px
      ".                .               ." 24px
      "product-list     product-list    product-list"
      / 96px 1fr auto;
    padding: 64px 0 0 0;

    &:first-child {
      padding: 0 0 64px;
    }
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    align-items: start;
    grid-template:
      "profile-picture  products-title"
      "profile-picture  product-list"
      ".                product-list" 16px
      "brand-info       product-list"
      / 256px;
    padding: 40px 0 0 0;
  }
`;

const BrandInfoArea = styled.div`
  grid-area: brand-info;
  align-self: center;
  overflow: hidden;
  ${responsiveMediaQueries.desktopAndAbove} {
    align-self: start;
  }
`;

const LabelContainer = styled(Typography)`
  max-width: 241px;

  ${responsiveMediaQueries.tabletAndAbove} {
    max-width: 435px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    max-width: 256px;
  }
`;

const PictureContainer = styled.div`
  grid-area: profile-picture;
  width: fit-content;
`;

const ProductsTitle = styled(Typography)`
  &&& {
    grid-area: products-title;
    margin-bottom: 15px;
  }
`;

const ProductPreviewCarouselWrapper = styled.div`
  grid-area: product-list;
`;

const PictureOverlay = styled.span`
  position: absolute;
  width: 64px;
  height: 64px;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.05);

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 96px;
    height: 96px;
  }
`;
